import type {
  ITaxonomyRepository,
  UpdatePromotedCategoriesResult,
  UpdateTaxonomyOnboardingResult,
} from "@/core/repositories/interfaces/ITaxonomyRepository";
import type { PromotedCategory } from "@/core/services/types/promoted-category.types";
import { parsePromotedCategories } from "@/core/services/types/promoted-category.types";
import type { TaxonomyItem } from "@/core/services/types/taxonomy.types";
import { createClient } from "@/lib/supabase";

export class TaxonomyRepository implements ITaxonomyRepository {
  async findAll(): Promise<TaxonomyItem[]> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("taxonomy")
      .select("*")
      .order("label", { ascending: true });

    if (error) {
      console.error("[TaxonomyRepository.findAll]", error.message);
      return [];
    }

    return data ?? [];
  }

  async updateOnboarding(
    id: string,
    onboarding: boolean,
  ): Promise<UpdateTaxonomyOnboardingResult> {
    const supabase = await createClient();

    const { data, error } = await supabase
      .from("taxonomy")
      .update({ onboarding })
      .eq("id", id)
      .in("category", ["ART_FORM", "ARTISTIC_PERIOD"])
      .select("*")
      .maybeSingle();

    if (error) {
      console.error("[TaxonomyRepository.updateOnboarding]", error.message);
      return { success: false, error: error.message };
    }

    if (!data) {
      return { success: false, error: "Taxonomy item not found" };
    }

    return { success: true, data };
  }

  async getPromotedCategories(): Promise<PromotedCategory[]> {
    const supabase = await createClient();
    const { data, error } = await supabase
      .from("app_settings")
      .select("promoted_categories")
      .eq("id", "default")
      .maybeSingle();

    if (error) {
      console.error("[TaxonomyRepository.getPromotedCategories]", error.message);
      return [];
    }

    return parsePromotedCategories(data?.promoted_categories);
  }

  async replacePromotedCategories(
    items: PromotedCategory[],
  ): Promise<UpdatePromotedCategoriesResult> {
    const supabase = await createClient();
    const { data, error } = await supabase
      .from("app_settings")
      .update({ promoted_categories: items })
      .eq("id", "default")
      .select("promoted_categories")
      .maybeSingle();

    if (error) {
      console.error(
        "[TaxonomyRepository.replacePromotedCategories]",
        error.message,
      );
      return { success: false, error: error.message };
    }

    if (!data) {
      return { success: false, error: "App settings not found" };
    }

    return {
      success: true,
      data: parsePromotedCategories(data.promoted_categories),
    };
  }
}
